// Les surfaces neutres utilisent un token privé pour suivre le thème, avec leur couleur claire en repli.
import React, { useState, useEffect } from 'react';
import { Select, DatePicker, Table, Card, Spin, Alert, Typography, Space, Tag } from 'antd';
import { supabase } from '../../utils/supabase';
import dayjs from 'dayjs';
import { getDeviseActive } from '../../utils/devise';

const { Title } = Typography;
const { Option } = Select;

// Copier/adapter la fonction de couleur de statut depuis OrdersList
const getOrderStatusColor = (status) => {
    switch (status) {
      case 'new': return 'blue';
      case 'pending': return 'warning';
      case 'confirmed': return 'processing';
      case 'in_delivery': return 'cyan';
      case 'delivered': return 'success';
      case 'canceled': return 'error';
      case 'completed': return 'success';
      default: return 'default';
    }
};

const DeliveriesMap = () => {
  // États
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [loadingDeliveryPersons, setLoadingDeliveryPersons] = useState(true);
  const [selectedDeliveryPersonId, setSelectedDeliveryPersonId] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs()); // Date par défaut: aujourd'hui
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loadingFilteredOrders, setLoadingFilteredOrders] = useState(false);
  const [errorLoadingOrders, setErrorLoadingOrders] = useState(null);

  // Charger les livreurs actifs
  const loadDeliveryPersons = async () => {
    try {
      setLoadingDeliveryPersons(true);
      const { data, error } = await supabase
        .from('delivery_persons')
        .select('id, name')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setDeliveryPersons(data || []);
    } catch (error) {
      console.error('Error loading delivery persons:', error);
      // Afficher une erreur pour le chargement des livreurs si nécessaire
    } finally {
      setLoadingDeliveryPersons(false);
    }
  };

  // Charger les commandes filtrées par livreur et date
  const loadFilteredOrders = async (personId, date) => {
    if (!personId || !date) {
        setFilteredOrders([]); // Vider si pas de sélection
        return;
    }
    
    setLoadingFilteredOrders(true);
    setErrorLoadingOrders(null);
    
    const startDate = dayjs(date).startOf('day').toISOString();
    const endDate = dayjs(date).endOf('day').toISOString();

    // console.log(`Fetching orders for person ${personId} between ${startDate} and ${endDate}`);

    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          order_number,
          status,
          total_amount,
          delivery_address,
          delivery_date, // Récupérer la date pour vérifier/afficher
          customer: customers ( name, phone )
        `)
        .eq('delivery_person_id', personId)
        .gte('delivery_date', startDate) // delivery_date >= début du jour
        .lte('delivery_date', endDate)   // delivery_date <= fin du jour
        .order('delivery_date', { ascending: true }); // Trier par heure de livraison prévue

      if (error) throw error;
      
      // console.log('Filtered orders:', data);
      setFilteredOrders(data || []);

    } catch (error) {
        console.error('Error loading filtered orders:', error);
        setErrorLoadingOrders(error.message || 'Erreur lors du chargement des commandes.');
        setFilteredOrders([]); // Vider en cas d'erreur
    } finally {
        setLoadingFilteredOrders(false);
    }
  };

  // Charger les livreurs au montage
  useEffect(() => {
    loadDeliveryPersons();
  }, []);

  // Recharger les commandes quand le livreur ou la date change
  useEffect(() => {
    if (selectedDeliveryPersonId && selectedDate) {
      loadFilteredOrders(selectedDeliveryPersonId, selectedDate);
    } else {
      setFilteredOrders([]); // Vider si sélection incomplète
    }
  }, [selectedDeliveryPersonId, selectedDate]);

  // Colonnes pour la table des commandes filtrées
  const columns = [
    {
      title: 'N°',
      dataIndex: 'order_number',
      key: 'order_number',
      width: 80,
      render: (num) => num || 'N/A',
    },
    {
      title: 'Client',
      dataIndex: 'customer',
      key: 'customer',
      render: (customer) => (
        <span>{customer?.name || 'Client inconnu'} ({customer?.phone || 'N/A'})</span>
      ),
    },
    {
      title: 'Adresse Livraison',
      dataIndex: 'delivery_address',
      key: 'delivery_address',
    },
    {
      title: 'Heure Prévue', // Afficher l'heure exacte si disponible
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 100,
      render: (date) => date ? dayjs(date).format('HH:mm') : 'N/A',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => <Tag color={getOrderStatusColor(status)}>{status || 'N/A'}</Tag>,
    },
    {
      title: 'Total',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 100,
      align: 'right',
      render: (amount) => `${Number(amount || 0).toFixed(2)} ${getDeviseActive()}`,
    },
    // Ajouter potentiellement une colonne Actions (ex: Marquer comme livré, voir sur carte)
  ];

  return (
    <div className="p-6">
      <Card>
        <Title level={4} style={{ marginBottom: '20px' }}>Livraisons par Livreur et Date</Title>
        
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Sélecteurs Livreur et Date */}
            <Space wrap>
                <Select
                    style={{ width: 250 }}
                    placeholder="Sélectionner un livreur"
                    loading={loadingDeliveryPersons}
                    value={selectedDeliveryPersonId}
                    onChange={setSelectedDeliveryPersonId}
                    allowClear
                >
                    {deliveryPersons.map(person => (
                        <Option key={person.id} value={person.id}>
                            {person.name}
                        </Option>
                    ))}
                </Select>

                <DatePicker 
                    value={selectedDate}
                    onChange={setSelectedDate}
                    format="DD/MM/YYYY" // Format de date français
                    allowClear={false} // Toujours avoir une date sélectionnée
                />
            </Space>

            {/* Section Liste des Commandes */}
            <div>
                <Title level={5}>Commandes à livrer</Title>
                {errorLoadingOrders && <Alert message={errorLoadingOrders} type="error" showIcon style={{ marginBottom: '15px' }} />} 
                <Table
                    columns={columns}
                    dataSource={filteredOrders}
                    rowKey="id"
                    loading={loadingFilteredOrders}
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 800 }}
                    // Ajouter ici potentiellement des actions sur les lignes ou la sélection
                />
            </div>

            {/* Placeholder pour la carte */}
            <div>
                 <Title level={5}>Carte des Livraisons</Title>
                 <div style={{ height: '400px', backgroundColor: 'var(--private-neutral-surface, #eee)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                     (Carte sera intégrée ici)
                 </div>
            </div>
        </Space>

      </Card>
    </div>
  );
};

export default DeliveriesMap; 